import React, { useState, useEffect, useRef } from 'react';
import { Layout, Menu, Alert, Modal, Typography, message } from 'antd';
import {
  UserOutlined, VideoCameraOutlined, MessageOutlined,
  DashboardOutlined, BarChartOutlined, UnorderedListOutlined,
  SettingOutlined, LogoutOutlined, NotificationOutlined
} from '@ant-design/icons';
import AccountPage from './pages/AccountPage';
import PublishPage from './pages/PublishPage';
import TaskListPage from './pages/TaskListPage';
import MonitorPage from './pages/MonitorPage';
import AnalyticsPage from './pages/AnalyticsPage';
import InteractionPage from './pages/InteractionPage';
import SettingsPage from './pages/SettingsPage';
import LoginPage from './pages/LoginPage';
import api, { isElectron, backend } from './api';
import { isMembershipActive } from './checkFeature';

const { Header } = Layout;
const { Text, Paragraph } = Typography;

function getInitialUser() {
  try {
    const raw = sessionStorage.getItem('auth_user');
    return raw ? JSON.parse(raw) : null;
  } catch (e) { return null; }
}

function App() {
  const [currentMenu, setCurrentMenu] = useState('accounts');
  const [electronReady, setElectronReady] = useState(null);
  const [user, setUser] = useState(getInitialUser);
  const [announcements, setAnnouncements] = useState([]);
  const [announceModalOpen, setAnnounceModalOpen] = useState(false);
  const [appConfig, setAppConfig] = useState({ appName: '', appLogo: '' });
  const scrollRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => { setElectronReady(isElectron); }, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    api.getAppConfig().then(cfg => {
      if (cfg) setAppConfig({ appName: cfg.appName || '', appLogo: cfg.appLogo || '' })
    })
  }, [user])

  const loadAnnouncements = async () => {
    try {
      const res = await backend.getAnnouncements();
      const list = (res.announcements || res.data?.announcements || [])
        .filter(a => a.status !== 'revoked')
        .reverse();
      setAnnouncements(list);
    } catch (e) {}
  };

  const handleLogin = (u) => {
    setUser(u);
    loadAnnouncements().then(() => {
      const seen = JSON.parse(sessionStorage.getItem('seen_announcements') || '[]');
      const unread = announcements.filter(a => !seen.includes(a.id));
      if (unread.length > 0) {
        setAnnounceModalOpen(true);
      }
    });
  };

  useEffect(() => {
    if (!user) return;
    loadAnnouncements();
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const key = 'startup_update_checked_3.0.0';
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    backend.checkVersion('3.0.0').then(res => {
      if (!res?.has_update) return;
      Modal.confirm({
        title: `发现新版本 ${res.version || res.latest_version || ''}`,
        content: (
          <div>
            {res.changelog && <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{res.changelog}</Paragraph>}
            {res.file_size ? <Paragraph>大小: {(res.file_size / 1024 / 1024).toFixed(2)} MB</Paragraph> : null}
            {res.force_update && <Alert type="warning" showIcon message="此版本为强制更新版本，请尽快安装。" />}
          </div>
        ),
        okText: '下载更新',
        cancelText: res.force_update ? '稍后处理' : '暂不更新',
        onOk: async () => {
          const download = await api.downloadUpdate(res);
          if (!download.success) {
            message.error(download.message || '下载失败');
            return Promise.reject(new Error(download.message || '下载失败'));
          }
          message.success('更新包下载完成');
          await api.openUpdateInstaller(download.path);
        }
      });
    }).catch(() => {});
  }, [user]);

  const handleLogout = () => {
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('auth_user');
    sessionStorage.removeItem('seen_announcements');
    setUser(null);
    setCurrentMenu('accounts');
    setAnnouncements([]);
  };

  const closeAnnounceModal = () => {
    const ids = announcements.map(a => a.id);
    sessionStorage.setItem('seen_announcements', JSON.stringify(ids));
    setAnnounceModalOpen(false);
  };

  const isAdmin = user?.role === 'admin';
  const membershipActive = user ? isMembershipActive(user) : false;

  useEffect(() => {
    if (user && !membershipActive && currentMenu !== 'settings') setCurrentMenu('settings');
  }, [user, membershipActive, currentMenu]);

  if (!user) {
    return <LoginPage onLogin={handleLogin} />;
  }

  const pages = {
    accounts: <AccountPage />,
    publish: <PublishPage />,
    tasks: <TaskListPage />,
    interaction: <InteractionPage />,
    monitor: <MonitorPage />,
    analytics: <AnalyticsPage />,
    settings: <SettingsPage user={user} />
  };

  const menuItems = membershipActive ? [
    { key: 'accounts', icon: <UserOutlined />, label: '账号管理' },
    { key: 'publish', icon: <VideoCameraOutlined />, label: '一键发布' },
    { key: 'tasks', icon: <UnorderedListOutlined />, label: '任务列表' },
    { key: 'interaction', icon: <MessageOutlined />, label: '粉丝互动' },
    { key: 'monitor', icon: <DashboardOutlined />, label: '数据监控' },
    { key: 'analytics', icon: <BarChartOutlined />, label: '数据统计' },
    { key: 'settings', icon: <SettingOutlined />, label: '设置' }
  ] : [
    { key: 'settings', icon: <SettingOutlined />, label: '设置' }
  ];

  const hasAnnouncements = announcements.length > 0;

  return (
    <Layout style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header style={{
        background: '#001529', padding: '0 24px', display: 'flex',
        alignItems: 'center', flexShrink: 0, gap: 16, overflow: 'hidden'
      }}>
        <div style={{ color: '#fff', fontSize: 18, fontWeight: 'bold', flexShrink: 0, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 8 }}>
          {appConfig.appLogo ? (
            <img src={appConfig.appLogo} alt="Logo" style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'contain' }} />
          ) : null}
          {appConfig.appName || '新媒体运营之家·矩阵'}
        </div>
        <Menu
          mode="horizontal"
          selectedKeys={[currentMenu]}
          style={{ flex: 1, background: 'transparent', minWidth: 0 }}
          theme="dark"
          items={menuItems}
          onClick={(e) => setCurrentMenu(e.key)}
        />

        {hasAnnouncements && (
          <div ref={scrollRef}
            style={{
              width: 260, flexShrink: 0, height: 64, overflow: 'hidden',
              position: 'relative'
            }}>
            <div style={{
              display: 'flex', flexDirection: 'column',
              color: '#ffd666', fontSize: 11, lineHeight: '21px',
              height: '100%', justifyContent: 'flex-start', paddingTop: 1
            }}>
              {announcements.slice(0, 3).map((a, i) => (
                <div key={a.id} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  opacity: 0.9
                }}>
                  <NotificationOutlined style={{ fontSize: 10, flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {a.title}
                  </span>
                </div>
              ))}
              {announcements.length > 3 && (
                <div style={{ opacity: 0.6, fontSize: 10, paddingLeft: 16 }}>
                  还有 {announcements.length - 3} 条公告...
                </div>
              )}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: '#fff', fontSize: 13, flexShrink: 0 }}>
          <NotificationOutlined
            style={{ cursor: 'pointer', fontSize: 15, opacity: hasAnnouncements ? 1 : 0.4 }}
            onClick={() => hasAnnouncements && setAnnounceModalOpen(true)}
          />
          <span style={{ opacity: 0.75 }}>
            {isAdmin ? '管理员' : '会员'}：{user.username}
          </span>
          <LogoutOutlined style={{ cursor: 'pointer', fontSize: 16 }} onClick={handleLogout} />
        </div>
      </Header>

      {electronReady === false && (
        <Alert
          type="warning" showIcon closable
          message="未检测到 Electron 环境"
          description="请在 Electron 应用窗口中进行操作，而非浏览器。npm run dev 会自动打开 Electron 窗口。"
          style={{ margin: 0, borderRadius: 0 }}
        />
      )}

      {!membershipActive && (
        <Alert
          type="warning"
          showIcon
          message="账号未开通使用时长"
          description="请在设置中使用有效激活码开通后再使用账号管理、发布任务等功能。"
          style={{ margin: 0, borderRadius: 0 }}
        />
      )}

      <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
        {membershipActive ? pages[currentMenu] : pages.settings}
      </div>

      <Modal
        title={<span><NotificationOutlined /> 系统公告</span>}
        open={announceModalOpen}
        onCancel={closeAnnounceModal}
        footer={null}
        width={520}
      >
        {announcements.map((a, i) => (
          <div key={a.id} style={{
            marginBottom: i < announcements.length - 1 ? 16 : 0,
            paddingBottom: i < announcements.length - 1 ? 16 : 0,
            borderBottom: i < announcements.length - 1 ? '1px solid #f0f0f0' : 'none'
          }}>
            <Text strong style={{ fontSize: 14 }}>{a.title}</Text>
            <Paragraph style={{ marginTop: 8, marginBottom: 4, whiteSpace: 'pre-wrap' }}>{a.content}</Paragraph>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {a.created_at ? new Date(a.created_at).toLocaleString('zh-CN') : ''}
            </Text>
          </div>
        ))}
        {announcements.length === 0 && (
          <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>暂无公告</div>
        )}
      </Modal>
    </Layout>
  );
}

export default App;
